import AiWriteButton from "../components/AiWriteButton";
import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import {
  Container,
  AppBar,
  Toolbar,
  Typography,
  TextField,
  Button,
  CircularProgress,
  Box,
  Grid,
  Alert,
  Switch,
  FormControlLabel,
  IconButton,
  MenuItem,
} from "@mui/material";
import { getDatabase, ref, onValue, set } from "firebase/database";
import { auth, db, uploadImage, uploadVideo } from "../firebase";
import { doc, getDoc, addDoc, collection, serverTimestamp } from "firebase/firestore";
import { AddPhotoAlternate as AddPhotoAlternateIcon } from "../icons";
import { VideoLibrary as VideoLibraryIcon } from "../icons";
import { Close as CloseIcon } from "../icons";
import BackButton from "./BackButton";
import { InputAdornment } from '@mui/material';
import { useSubscriptionContext } from "./SubscriptionContext";
import { T } from "./Translate";
import { getCountryFromIP, OFFER_COMMISSION_RATE, getPendingOfferCommission, markOfferCommissionPaid } from "./homeUtils";
import PaypalButton from "../components/PaypalButton";
import { notifyFollowers } from "../utils/notifications";
import { pageContentSx } from "../pageLayout";
import { barColors } from "../theme";
import { uploadButtonSx, uploadProgressSx, mediaPreviewStyle, mediaPreviewBoxSx, removeMediaButtonSx, removeMediaIconSx } from "./mediaFormStyles";
import PaymentInfoFields from "../components/PaymentInfoFields";
import CouponExpiryFields, { couponExpiryToDoc } from "../components/CouponExpiryFields";
import { OFFER_CATEGORIES } from "../offerCategories";

const containsOffensiveLanguage = (text) => {
  // Basic list of offensive words - expand as needed
  const offensiveWords = [
    "pija",
    "puto",
    "hijo de puta",
    "travesti",
    "trolo",
    "puta",
    "pijo",
    "hijo de puto",
    "gay",
    "hijo de re mil",
    "hijo de re mil puta",
    "chupa pija",
    "trolazo",
    "pene",
    "pito",
  ];
  
  // Convert to lowercase for case-insensitive matching
  const lowerText = text.toLowerCase();
  
  // Check for exact matches and partial matches
  return offensiveWords.some(word => 
    lowerText.includes(word.toLowerCase()) ||
    // Check for common letter substitutions
    lowerText.replace(/[4@]/g, 'a')
             .replace(/[3]/g, 'e')
             .replace(/[1!]/g, 'i')
             .replace(/[0]/g, 'o')
             .includes(word.toLowerCase())
  );
};

const PublishOffer = () => {
  const [formData, setFormData] = useState({
    product: "",
    description: "",
    category: "",
    offer: "",
    url: "",
    cupon: "",
    price: "",
    images: [],
    videos: [],
  });
  const [loading, setLoading] = useState(false);
  const [publishSuccess, setPublishSuccess] = useState(false);
  const [errors, setErrors] = useState({});
  // Cuando está activo, la oferta se publica sin el campo "country" para que
  // filterOffersByCountry (homeUtils.js) la muestre a usuarios de cualquier país,
  // en vez de solo a los del país detectado por IP al momento de publicar.
  const [worldwide, setWorldwide] = useState(false);
  const [paymentInfo, setPaymentInfo] = useState({
    installments: "",
    installmentsNoInterest: false,
    promo: "",
    acceptedCards: [],
  });
  const [couponExpiry, setCouponExpiry] = useState({ expiresAt: "", maxUses: "" });
  // Cobro con PayPal dentro de Notiline (ver OfferCheckout.js): el comprador
  // paga acá y acuerda el envío por chat, así que el cupón (que se usa en la
  // web del vendedor) no aplica y se oculta. El enlace pasa a ser opcional.
  const [sellWithPaypal, setSellWithPaypal] = useState(false);
  const [paypalEmail, setPaypalEmail] = useState("");
  const navigate = useNavigate();
  const { subscription, isSubscriptionActive } = useSubscriptionContext(); // Correct usage


  useEffect(() => {
    if (!subscription) {
      navigate('/subscription');
    }
  }, [subscription, navigate]);

  const PLAN_LIMITS_OFFERS = { Free: 3, Básico: 3, Profesional: 10, Premium: Infinity };
  const [offerCount, setOfferCount] = useState(0);

  useEffect(() => {
    const user = auth.currentUser;
    if (!user || !subscription) return;
    const rtdb = getDatabase();
    const now = new Date();
    const monthYear = `${now.getMonth() + 1}-${now.getFullYear()}`;
    const countsRef = ref(rtdb, `publicationCounts/${user.uid}/${monthYear}`);
    const unsubscribe = onValue(countsRef, (snap) => {
      setOfferCount(snap.exists() ? snap.val().offers || 0 : 0);
    });
    return () => unsubscribe();
  }, [subscription]);

  const offerLimit = PLAN_LIMITS_OFFERS[subscription?.planName] ?? 0;
  // Comisión de ventas anteriores por PayPal: igual que en tiendas, mientras
  // esté pendiente no se puede publicar otra oferta.
  const [pendingCommission, setPendingCommission] = useState({ amount: 0, salesCount: 0 });
  const [commissionFeedback, setCommissionFeedback] = useState(null);

  const refreshPendingCommission = React.useCallback(() => {
    getPendingOfferCommission(auth.currentUser?.uid).then(setPendingCommission);
  }, []);

  // Espera a la sesión: al recargar la página, currentUser todavía está vacío.
  useEffect(() => auth.onAuthStateChanged(refreshPendingCommission), [refreshPendingCommission]);

  const canPublishOffer = isSubscriptionActive && offerCount < offerLimit && pendingCommission.amount <= 0;
  const offerLimitMsg = offerCount >= offerLimit
    ? <T>Has alcanzado el límite de {offerLimit} oferta{offerLimit !== 1 ? "s" : ""} por mes de tu plan {subscription?.planName}. Mejora tu plan para publicar más.</T>
    : "";

  // Ofertas destacadas: beneficio de los planes Profesional/Premium, sube la
  // oferta en el orden de relevancia del feed (ver isPromoted en
  // calculateOfferScore, homeUtils.js). Básico no tiene cupo.
  const FEATURED_LIMITS_OFFERS = { Básico: 0, Profesional: 1, Premium: 3 };
  const [featured, setFeatured] = useState(false);
  const [featuredCount, setFeaturedCount] = useState(0);

  useEffect(() => {
    const user = auth.currentUser;
    if (!user || !subscription) return;
    const rtdb = getDatabase();
    const now = new Date();
    const monthYear = `${now.getMonth() + 1}-${now.getFullYear()}`;
    const countsRef = ref(rtdb, `publicationCounts/${user.uid}/${monthYear}`);
    const unsubscribe = onValue(countsRef, (snap) => {
      setFeaturedCount(snap.exists() ? snap.val().featuredOffers || 0 : 0);
    });
    return () => unsubscribe();
  }, [subscription]);

  const featuredLimit = FEATURED_LIMITS_OFFERS[subscription?.planName] ?? 0;
  const canFeatureOffer = featuredLimit > 0 && featuredCount < featuredLimit;

  const validateForm = () => {
    let isValid = true;
    const newErrors = {};
  
    // Existing validations
    Object.keys(formData).forEach((key) => {
      // El precio es opcional: una tienda puede publicar un cupón general
      // que vale para todos sus productos, sin un producto/precio puntual.
      const optionalWithPaypal = sellWithPaypal && (key === "cupon" || key === "url");
      if (key !== "images" && key !== "videos" && key !== "price" && !optionalWithPaypal && formData[key].trim() === "") {
        newErrors[key] = <T>Este campo es obligatorio</T>;
        isValid = false;
      }
    });

    // Check for offensive language in text fields
    const textFields = ['product', 'description', 'cupon'];
    textFields.forEach(field => {
      if (formData[field] && containsOffensiveLanguage(formData[field])) {
        newErrors[field] = <T>Por favor, evite usar lenguaje inapropiado</T>;
        isValid = false;
      }
    });

    // Price validation (solo si se cargó un precio)
    if (formData.price) {
      const price = parseFloat(formData.price);
      if (isNaN(price)) {
        newErrors.price = <T>Ingrese un precio válido</T>;
        isValid = false;
      } else if (price <= 0) {
        newErrors.price = <T>El precio debe ser mayor a 0</T>;
        isValid = false;
      } else if (price > 999999.99) {
        newErrors.price = <T>El precio no puede exceder 999,999.99</T>;
        isValid = false;
      }
    }

    if (sellWithPaypal) {
      if (!formData.price) {
        newErrors.price = <T>Para cobrar con PayPal la oferta necesita un precio</T>;
        isValid = false;
      }
      const cleanPaypalEmail = paypalEmail.trim();
      if (!cleanPaypalEmail) {
        newErrors.paypalEmail = <T>Ingresá tu correo de PayPal para recibir los pagos</T>;
        isValid = false;
      } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(cleanPaypalEmail)) {
        newErrors.paypalEmail = <T>Ingresá un correo de PayPal válido</T>;
        isValid = false;
      }
    }

    // Existing media validation
    if (formData.images.length === 0 && formData.videos.length === 0) {
      newErrors.media = <T>Debes subir al menos una imagen o un video</T>;
      isValid = false;
    }
  
    setErrors(newErrors);
    return isValid;
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    
    // Check for offensive language as user types
    if (['product', 'description', 'cupon'].includes(name) && containsOffensiveLanguage(value)) {
      setErrors(prev => ({
        ...prev,
        [name]: <T>Por favor, evite usar lenguaje inapropiado</T>
      }));
    } else {
      setErrors(prev => ({ ...prev, [name]: "" }));
    }
    
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handlePublishOffer = async () => {
    if (pendingCommission.amount > 0 || !validateForm()) {
      return;
    }

    const user = auth.currentUser;

    if (!user) {
      console.error("No user is signed in");
      return;
    }

    setLoading(true);

    try {
      const { uid } = user;
      const userDocRef = doc(db, "users", uid);
      const userDocSnapshot = await getDoc(userDocRef);

      if (userDocSnapshot.exists()) {
        const userData = userDocSnapshot.data();
        // uid siempre viene de auth, no de userData: el doc de perfil no
        // necesariamente guarda su propio uid como campo (es el id del doc).
        const userId = uid;
        const { displayName: userName, profileImageUrl: imageProfile } = userData;
        const country = worldwide ? null : await getCountryFromIP();
        const isFeatured = featured && canFeatureOffer;

        const offerDocRef = await addDoc(collection(db, "offers"), {
          uid: userId,
          name: userName,
          image: imageProfile,
          product: formData.product,
          category: formData.category,
          offer: formData.offer,
          price: formData.price,
          url: formData.url,
          cupon: sellWithPaypal ? "" : formData.cupon,
          ...couponExpiryToDoc(sellWithPaypal ? { expiresAt: "", maxUses: "" } : couponExpiry),
          paypalEmail: sellWithPaypal ? paypalEmail.trim() : null,
          cuponUses: 0,
          description: formData.description,
          images: formData.images,
          videos: formData.videos,
          installments: paymentInfo.installments,
          installmentsNoInterest: paymentInfo.installmentsNoInterest,
          promo: paymentInfo.promo,
          acceptedCards: paymentInfo.acceptedCards,
          country,
          isPromoted: isFeatured,
          createdAt: serverTimestamp(),
        });

        if (isFeatured) {
          const rtdb = getDatabase();
          const now = new Date();
          const monthYear = `${now.getMonth() + 1}-${now.getFullYear()}`;
          await set(ref(rtdb, `publicationCounts/${userId}/${monthYear}/featuredOffers`), featuredCount + 1);
        }

        notifyFollowers({
          ownerUid: userId,
          category: "offers",
          type: "offer",
          message: `${userName} publicó una nueva oferta: ${formData.product}`,
          postId: offerDocRef.id,
        });

        setPublishSuccess(true);
      } else {
        console.error("User document does not exist");
      }
    } catch (error) {
      console.error("Error publishing offer:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleImageUpload = async (event) => {
    const files = event.target.files;

    if (!files || files.length === 0) {
      setErrors((prev) => ({
        ...prev,
        media: <T>No se seleccionó ninguna imagen</T>,
      }));
      return;
    }

    setLoading(true);

    try {
      const imageUrls = [];

      for (const file of files) {
        const imageUrl = await uploadImage(file);
        imageUrls.push(imageUrl);
      }

      setFormData((prevFormData) => ({
        ...prevFormData,
        images: [...prevFormData.images, ...imageUrls],
      }));
    } catch (error) {
      console.error("Error uploading images:", error);
      setErrors((prev) => ({
        ...prev,
        media: error?.message || <T>No se pudo cargar la imagen seleccionada</T>,
      }));
    } finally {
      setLoading(false);
    }
  };

  const handleVideoUpload = async (event) => {
    const files = event.target.files;

    if (!files || files.length === 0) {
      setErrors((prev) => ({
        ...prev,
        media: <T>No se seleccionó ningún video</T>,
      }));
      return;
    }

    setLoading(true);

    try {
      const videoUrls = [];

      for (const file of files) {
        const videoUrl = await uploadVideo(file);
        videoUrls.push(videoUrl);
      }

      setFormData((prevFormData) => ({
        ...prevFormData,
        videos: [...prevFormData.videos, ...videoUrls],
      }));
    } catch (error) {
      console.error("Error uploading videos:", error);
      setErrors((prev) => ({
        ...prev,
        media: error?.message || <T>No se pudo cargar el video seleccionado</T>,
      }));
    } finally {
      setLoading(false);
    }
  };

  const handleRemoveMedia = (type, index) => {
    setFormData((prevFormData) => ({
      ...prevFormData,
      [type]: prevFormData[type].filter((_, i) => i !== index),
    }));
  };
  const history = useNavigate();
  const handleRedirectHome = () => {
    history("/home");
  };

  return (
    <Container maxWidth={false} disableGutters sx={pageContentSx}>
      <AppBar position="fixed" sx={{ bgcolor: barColors.background, color: barColors.text }}>
        <Toolbar>
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 1,
              width: "100%",
              maxWidth: { xs: "100%", md: "980px" },
              mx: "auto",
              px: 2,
              boxSizing: "border-box",
            }}
          >
            <BackButton color="inherit" />
            <Typography variant="h5" noWrap>
              <T>Publicar Oferta</T>
            </Typography>
          </Box>
        </Toolbar>
      </AppBar>
      <Container sx={{ mt: 14, mb: 10 }}>
        {commissionFeedback && (
          <Alert severity={commissionFeedback.type} sx={{ mb: 2 }} onClose={() => setCommissionFeedback(null)}>
            {commissionFeedback.message}
          </Alert>
        )}
        {pendingCommission.amount > 0 && (
          <Alert severity="warning" icon={false} sx={{ mb: 2, "& .MuiAlert-message": { width: "100%" } }}>
            <Typography variant="body2" sx={{ mb: 1.5 }}>
              <T>Tenés ${pendingCommission.amount.toFixed(2)} de comisión pendiente por {pendingCommission.salesCount} venta(s) por PayPal. Pagala para poder publicar otra oferta.</T>
            </Typography>
            <PaypalButton
              amount={pendingCommission.amount}
              description="Comisión Notiline - Ofertas"
              clientId={process.env.REACT_APP_PAYPAL_CLIENT_ID}
              payeeEmail={process.env.REACT_APP_PLATFORM_PAYPAL_EMAIL}
              metadata={{ customId: auth.currentUser?.uid }}
              onApprove={(details) => {
                markOfferCommissionPaid({ sellerId: auth.currentUser.uid, paypalTransactionId: details.id })
                  .then(() => {
                    setCommissionFeedback({ type: "success", message: <T>Comisión pagada. ¡Gracias!</T> });
                    refreshPendingCommission();
                  })
                  .catch((error) => {
                    console.error("Error al registrar el pago de comisión:", error);
                    setCommissionFeedback({ type: "warning", message: <T>El pago se realizó pero no pudimos registrarlo. Contactanos.</T> });
                  });
              }}
              onError={() => setCommissionFeedback({ type: "error", message: <T>Ocurrió un error al procesar el pago.</T> })}
            />
          </Alert>
        )}
        <TextField
          autoFocus
          margin="dense"
          id="product"
          label={<T>Nombre del producto</T>}
          type="text"
          fullWidth
          required
          name="product"
          value={formData.product}
          onChange={handleInputChange}
          error={!!errors.product}
          helperText={errors.product}
        />
        <TextField
          margin="dense"
          id="description"
          label={<T>Descripción del producto: Hasta 256 caracteres</T>}
          type="text"
          fullWidth
          required
          name="description"
          rows={2}
          inputProps={{
            maxLength: 256
          }}
          multiline
          value={formData.description}
          onChange={handleInputChange}
          error={!!errors.description}
          helperText={errors.description}
        />
        <AiWriteButton
          type="offer"
          maxDescription={256}
          fields={{
            producto: formData.product,
            descripcion: formData.description,
            categoria: formData.category,
            oferta: formData.offer,
            precio: formData.price,
            cupon: formData.cupon,
          }}
          onResult={({ title, description }) =>
            setFormData((prev) => ({ ...prev, product: title || prev.product, description: description || prev.description }))
          }
        />
        <TextField
          select
          margin="dense"
          id="category"
          label={<T>Categoría</T>}
          fullWidth
          required
          name="category"
          value={formData.category}
          onChange={handleInputChange}
          error={!!errors.category}
          helperText={errors.category}
        >
          {OFFER_CATEGORIES.map((c) => (
            <MenuItem key={c.id} value={c.id}>
              <T>{c.label}</T>
            </MenuItem>
          ))}
        </TextField>
        <TextField
  margin="dense"
  id="price"
  label={sellWithPaypal ? <T>Precio en USD</T> : <T>Precio en USD (opcional)</T>}
  type="text"
  fullWidth
  name="price"
  value={formData.price}
  onChange={(e) => {
    const value = e.target.value;
    
    // Solo permite números y un punto decimal
    if (/^\d*\.?\d*$/.test(value)) {
      // Si hay punto decimal, limita a 2 decimales
      if (value.includes('.')) {
        const [whole, decimal] = value.split('.');
        const formattedValue = `${whole}.${decimal.slice(0, 2)}`;
        setFormData(prev => ({
          ...prev,
          price: formattedValue
        }));
      } else {
        setFormData(prev => ({
          ...prev,
          price: value
        }));
      }
    }
  }}
  onBlur={(e) => {
    // Al perder el foco, asegura formato con 2 decimales
    const value = e.target.value;
    if (value) {
      const numberValue = parseFloat(value);
      if (!isNaN(numberValue)) {
        setFormData(prev => ({
          ...prev,
          price: numberValue.toFixed(2)
        }));
      }
    }
  }}
  error={!!errors.price}
  required={sellWithPaypal}
  helperText={
    errors.price ||
    (sellWithPaypal
      ? <T>Poné el precio real, sin descuento. El precio final se calcula automáticamente con el % de descuento.</T>
      : <T>Poné el precio real, sin descuento. El precio final se calcula automáticamente con el % de descuento. Dejalo vacío si el cupón vale para todos los productos de la tienda.</T>)
  }
  InputProps={{
    startAdornment: <InputAdornment position="start">$</InputAdornment>
  }}
/>
        <TextField
          margin="dense"
          id="offer"
          label={<T>Descuento %</T>}
          type="text"
          fullWidth
          required
          name="offer"
          value={formData.offer}
          onChange={handleInputChange}
          error={!!errors.offer}
          helperText={errors.offer}
        />
        <FormControlLabel
          sx={{ mt: 1, display: "flex" }}
          control={
            <Switch
              checked={sellWithPaypal}
              onChange={(e) => {
                setSellWithPaypal(e.target.checked);
                setErrors((prev) => ({ ...prev, cupon: "", url: "", price: "", paypalEmail: "" }));
              }}
            />
          }
          label={<T>Cobrar con PayPal en Notiline</T>}
        />
        {sellWithPaypal && (
          <Box mb={1}>
            <TextField
              margin="dense"
              label={<T>Tu correo de PayPal</T>}
              type="email"
              fullWidth
              required
              value={paypalEmail}
              onChange={(e) => {
                setPaypalEmail(e.target.value);
                setErrors((prev) => ({ ...prev, paypalEmail: "" }));
              }}
              error={!!errors.paypalEmail}
              helperText={
                errors.paypalEmail ||
                <T>Los compradores pagan el precio con descuento directo a este correo y después acuerdan el envío con vos por chat.</T>
              }
            />
            <Alert severity="info" sx={{ mt: 1 }}>
              <T>Notiline cobra {OFFER_COMMISSION_RATE * 100}% de comisión sobre cada venta por PayPal. La pagás antes de tu próxima publicación.</T>
            </Alert>
          </Box>
        )}
        <TextField
          margin="dense"
          id="url"
          label={sellWithPaypal ? <T>Ejemplo.com/producto (opcional)</T> : <T>Ejemplo.com/producto</T>}
          type="text"
          fullWidth
          required={!sellWithPaypal}
          name="url"
          value={formData.url}
          onChange={handleInputChange}
          error={!!errors.url}
          helperText={errors.url}
        />
        {!sellWithPaypal && (
          <>
            <TextField
              margin="dense"
              id="cupon"
              label={<T>Nombre del cupón de descuento</T>}
              type="text"
              fullWidth
              required
              name="cupon"
              value={formData.cupon}
              onChange={handleInputChange}
              error={!!errors.cupon}
              helperText={errors.cupon}
            />
            <CouponExpiryFields value={couponExpiry} onChange={setCouponExpiry} />
          </>
        )}
        <PaymentInfoFields value={paymentInfo} onChange={setPaymentInfo} />
        <FormControlLabel
          sx={{ mt: 1, display: "flex" }}
          control={
            <Switch
              checked={worldwide}
              onChange={(e) => setWorldwide(e.target.checked)}
            />
          }
          label={<T>Visible en todos los países (ignora tu ubicación actual)</T>}
        />
        {featuredLimit > 0 && (
          <>
            <FormControlLabel
              sx={{ mt: 1, display: "flex" }}
              control={
                <Switch
                  checked={featured}
                  onChange={(e) => setFeatured(e.target.checked)}
                  disabled={!canFeatureOffer}
                />
              }
              label={<T>Destacar esta oferta (aparece primero en el feed)</T>}
            />
            <Typography variant="caption" color={featuredCount >= featuredLimit ? "error" : "text.secondary"}>
              <T>Destacadas este mes: {featuredCount} / {featuredLimit}</T>
            </Typography>
          </>
        )}
        <label htmlFor="upload-image-button" style={{ width: "100%" }}>
          <Button
            variant="contained"
            fullWidth
            component="div"
            sx={uploadButtonSx(!!errors.media)}
            disabled={loading}
          >
            <AddPhotoAlternateIcon sx={{ color: "white" }} />
            <T>Subir Imágenes del producto</T>
            {loading && (
              <CircularProgress size={40} thickness={4} sx={uploadProgressSx} />
            )}
          </Button>
        </label>
        <input
          type="file"
          accept="image/*"
          onClick={(event) => (event.target.value = null)}
          onChange={handleImageUpload}
          style={{ display: "none" }}
          id="upload-image-button"
          multiple
        />

        <label htmlFor="upload-video-button" style={{ width: "100%" }}>
          <Button
            variant="contained"
            fullWidth
            component="div"
            sx={{ ...uploadButtonSx(!!errors.media), mt: 1 }}
            disabled={loading}
          >
            <VideoLibraryIcon sx={{ color: "white" }} />
            <T>Subir Video del producto</T>
            {loading && (
              <CircularProgress size={40} thickness={4} sx={uploadProgressSx} />
            )}
          </Button>
        </label>
        <input
          type="file"
          accept="video/*"
          onClick={(event) => (event.target.value = null)}
          onChange={handleVideoUpload}
          style={{ display: "none" }}
          id="upload-video-button"
          multiple
        />

        {errors.media && (
          <Typography color="error" variant="caption">
            {errors.media}
          </Typography>
        )}

        <Grid container sx={{ mt: 2 }}>
          {formData.images.map((imageUrl, index) => (
            <Grid item xs={12} sm={6} md={4} key={index} margin={1}>
              <Box sx={mediaPreviewBoxSx(false)}>
                <img
                  src={imageUrl}
                  alt="Imagen del producto"
                  style={mediaPreviewStyle}
                />
                <IconButton sx={removeMediaButtonSx} onClick={() => handleRemoveMedia("images", index)}>
                  <CloseIcon sx={removeMediaIconSx} />
                </IconButton>
              </Box>
            </Grid>
          ))}
          {formData.videos.map((videoUrl, index) => (
            <Grid item xs={12} sm={6} md={4} key={index} margin={1}>
              <Box sx={{ position: "relative", width: "100%", maxWidth: 320 }}>
                <video
                  src={videoUrl}
                  controls
                  style={{ width: "100%", borderRadius: "10px" }}
                />
                <IconButton sx={removeMediaButtonSx} onClick={() => handleRemoveMedia("videos", index)}>
                  <CloseIcon sx={removeMediaIconSx} />
                </IconButton>
              </Box>
            </Grid>
          ))}
        </Grid>
        {publishSuccess && (
          <Box sx={{ textAlign: "center" }}>
            <Typography><T>La oferta se ha publicado correctamente.</T></Typography>
            <Button onClick={handleRedirectHome} color="primary">
              <T>Ir a Ofertas</T>
            </Button>
          </Box>
        )}
        {!publishSuccess && (
          <>
            <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mt: 2 }}>
              <Typography variant="caption" color={offerCount >= offerLimit ? "error" : "text.secondary"}>
                <T>Ofertas este mes: {offerCount} / {offerLimit === Infinity ? "∞" : offerLimit}</T>
              </Typography>
            </Box>
            {offerLimitMsg && (
              <Alert severity="warning" sx={{ mt: 1 }} action={
                <Button size="small" color="inherit" onClick={() => navigate("/subscription")}><T>Ver planes</T></Button>
              }>{offerLimitMsg}</Alert>
            )}
            <Button
              variant="contained"
              sx={{ mt: 2 }}
              fullWidth
              onClick={handlePublishOffer}
              color="primary"
              disabled={loading || !canPublishOffer}
            >
              {loading ? <T>Publicando...</T> : <T>Publicar</T>}
            </Button>
          </>
        )}
      </Container>
    </Container>
  );
};

export default PublishOffer;
